import React from "react";
import { useTranslation } from "react-i18next";

import { Box, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { CurrencyType } from "store/types";
import { getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";

import { LoanAmount } from "../../form-fields/LoanAmount";
import { FormValues } from "../../schemas";
import { QuestionSlot } from "../QuestionSlot";
import { WithdrawalValues } from "../types";

interface Step8Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  hasPrincipalSchedule: boolean;
  hasRegularInterest: boolean;
  hasPIK: boolean;
  withdrawalValues: WithdrawalValues;
  setWithdrawalValues: React.Dispatch<React.SetStateAction<WithdrawalValues>>;
  currency: CurrencyType;
  isNewPeriod?: boolean;
}

export const Step8Drawdown: React.FC<Step8Props> = ({
  formik,
  dateFormat,
  hasPrincipalSchedule,
  hasRegularInterest,
  hasPIK,
  withdrawalValues,
  setWithdrawalValues,
  currency,
  isNewPeriod = false
}) => {
  const { t } = useTranslation("debt");
  const { values } = formik;
  const showStatusDate = Boolean(values.loan_status_frequency);
  const interestSpreadInAdvance =
    hasRegularInterest && values.float_type === "spread" && values.interest_rate_fixing === "in_advance";
  const pikSpreadInAdvance = hasPIK && values.pik_float_type === "spread" && values.pik_rate_fixing === "in_advance";

  return (
    <Box>
      <QuestionSlot visible label={t("wizard.drawdown.nameQuestion")}>
        <TextField
          fullWidth
          label={t("wizard.drawdown.nameLabel")}
          value={withdrawalValues.deal_title}
          onChange={(e) => setWithdrawalValues((p) => ({ ...p, deal_title: e.target.value }))}
          placeholder={t("wizard.drawdown.namePlaceholder")}
        />
      </QuestionSlot>

      <QuestionSlot visible label={t("wizard.drawdown.amountQuestion")}>
        <LoanAmount
          formik={
            {
              values: { loan_amount: withdrawalValues.loan_amount } as FormValues,
              touched: {},
              errors: {},
              setFieldValue: (_field: string, val: number) => {
                setWithdrawalValues((p) => ({ ...p, loan_amount: val }));
              }
            } as unknown as FormikProps<FormValues>
          }
          currency={currency}
          label={t("wizard.drawdown.amountLabel")}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={withdrawalValues.loan_amount > 0}
        label={isNewPeriod ? t("wizard.drawdown.changeDateQuestion") : t("wizard.drawdown.dateQuestion")}
      >
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          label={t("wizard.drawdown.dateLabel")}
          value={withdrawalValues.start_date ? dayjs(withdrawalValues.start_date) : null}
          onChange={(v) => {
            if (!v) return;
            const iso = toIsoMidday(v);
            setWithdrawalValues((p) => ({
              ...p,
              start_date: iso,
              ...(showStatusDate && values.loan_status_frequency
                ? { loan_first_status_date: getEndOfPeriod(v, values.loan_status_frequency) }
                : {}),
              ...(hasPrincipalSchedule && values.principal_frequency
                ? { principal_first_payment_date: getEndOfPeriod(v, values.principal_frequency) }
                : {}),
              ...(hasRegularInterest && values.interest_frequency
                ? { interest_first_payment_date: getEndOfPeriod(v, values.interest_frequency) }
                : {}),
              ...(hasPIK && values.pik_capitalization_frequency
                ? { pik_first_capitalization_date: getEndOfPeriod(v, values.pik_capitalization_frequency) }
                : {}),
              ...(values.loan_reset_frequency
                ? { loan_reset_date: getEndOfPeriod(v, values.loan_reset_frequency) }
                : {})
            }));
          }}
          sx={{ width: "100%" }}
        />
      </QuestionSlot>

      {showStatusDate && (
        <QuestionSlot visible={withdrawalValues.loan_amount > 0} label={t("wizard.drawdown.firstStatusDateQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.drawdown.firstStatusDateLabel")}
            value={withdrawalValues.loan_first_status_date ? dayjs(withdrawalValues.loan_first_status_date) : null}
            onChange={(v) => setWithdrawalValues((p) => ({ ...p, loan_first_status_date: v?.isValid() ? toIsoMidday(v) : null }))}
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculatedFromDrawdown") } }}
          />
        </QuestionSlot>
      )}

      {hasPrincipalSchedule && (
        <QuestionSlot visible={withdrawalValues.loan_amount > 0} label={t("wizard.drawdown.firstPrincipalQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.drawdown.firstPrincipalLabel")}
            value={
              withdrawalValues.principal_first_payment_date
                ? dayjs(withdrawalValues.principal_first_payment_date)
                : null
            }
            onChange={(v) =>
              setWithdrawalValues((p) => ({ ...p, principal_first_payment_date: v?.isValid() ? toIsoMidday(v) : null }))
            }
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculatedFromDrawdown") } }}
          />
        </QuestionSlot>
      )}

      {hasRegularInterest && (
        <QuestionSlot visible={withdrawalValues.loan_amount > 0} label={t("wizard.drawdown.firstInterestQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.drawdown.firstInterestLabel")}
            value={
              withdrawalValues.interest_first_payment_date ? dayjs(withdrawalValues.interest_first_payment_date) : null
            }
            onChange={(v) =>
              setWithdrawalValues((p) => ({ ...p, interest_first_payment_date: v?.isValid() ? toIsoMidday(v) : null }))
            }
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculatedFromDrawdown") } }}
          />
        </QuestionSlot>
      )}

      {hasPIK && (
        <QuestionSlot visible={withdrawalValues.loan_amount > 0} label={t("wizard.drawdown.firstPIKQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.drawdown.firstPIKLabel")}
            value={
              withdrawalValues.pik_first_capitalization_date
                ? dayjs(withdrawalValues.pik_first_capitalization_date)
                : null
            }
            onChange={(v) =>
              setWithdrawalValues((p) => ({ ...p, pik_first_capitalization_date: v?.isValid() ? toIsoMidday(v) : null }))
            }
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculatedFromDrawdown") } }}
          />
        </QuestionSlot>
      )}

      {(interestSpreadInAdvance || pikSpreadInAdvance || withdrawalValues.loan_reset_date) && (
        <QuestionSlot
          visible={withdrawalValues.loan_amount > 0}
          label={t("wizard.interestTerms.loanResetDateQuestion")}
        >
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.interestTerms.loanResetDateLabel")}
            value={withdrawalValues.loan_reset_date ? dayjs(withdrawalValues.loan_reset_date) : null}
            onChange={(v) =>
              setWithdrawalValues((p) => ({ ...p, loan_reset_date: v?.isValid() ? toIsoMidday(v) : null }))
            }
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: !interestSpreadInAdvance && !pikSpreadInAdvance,
                helperText:
                  !interestSpreadInAdvance && !pikSpreadInAdvance
                    ? t("wizard.interestTerms.loanResetDateNotApplicableWarning")
                    : t("wizard.common.autoCalculatedFromDrawdown")
              }
            }}
          />
        </QuestionSlot>
      )}
    </Box>
  );
};
